import React, { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { RiHistoryLine, RiTimeLine } from 'react-icons/ri';
import projectManager from '../core/ProjectManager';
import useSceneStore from '../store/useSceneStore';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import useDialogStore from '../store/useDialogStore';
import useNotificationStore from '../store/useNotificationStore';

const { FiX, FiRotateCcw } = FiIcons;

const HistoryPanel = ({ isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const { projectId, projectName } = useSceneStore();
  const { openDialog } = useDialogStore();
  const { addNotification } = useNotificationStore();

  const loadHistory = async () => {
    if (!projectId) {
      setHistory([]);
      return;
    }

    if (typeof projectManager.getProjectHistory === 'function') {
      const historyResult = await projectManager.getProjectHistory(projectId);

      if (historyResult?.success) {
        const versions = (historyResult?.data ?? []).map((item) => {
          let projectData = item?.data ?? '';

          try {
            projectData = typeof projectData === 'string'
              ? JSON.parse(projectData)
              : projectData;
          } catch {
            projectData = null;
          }

          return {
            id: item?.id,
            timestamp: item?.timestamp ?? Date.now(),
            thumbnail: item?.thumbnail ?? '',
            data: projectData
          };
        }).filter((item) => item?.data);

        setHistory(versions);
        return;
      }
    }

    const result = await projectManager.getProjects();
    if (!result?.success) {
      setHistory([]);
      return;
    }

    const project = (result?.data ?? []).find(
      (item) => String(item?.id) === String(projectId)
    );

    if (!project) {
      setHistory([]);
      return;
    }

    let projectData = project?.data ?? '';

    try {
      projectData = typeof projectData === 'string'
        ? JSON.parse(projectData)
        : projectData;
    } catch {
      projectData = null;
    }

    if (!projectData) {
      setHistory([]);
      return;
    }

    setHistory([{
      id: project?.id,
      timestamp: project?.updatedAt ?? Date.now(),
      thumbnail: project?.thumbnail ?? '',
      data: projectData
    }]);
  };

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen, projectId]);

  const handleRestore = (item) => {
    openDialog({
      title: 'Przywróć wersję',
      message: `Czy na pewno chcesz przywrócić wersję z ${format(item.timestamp, 'HH:mm, d MMM yyyy', { locale: pl })}? Obecne niezapisane zmiany zostaną utracone.`,
      confirmText: 'Przywróć',
      variant: 'default',
      onConfirm: () => {
        projectManager.loadState(item.data);
        onClose();
        addNotification('success', 'Wersja projektu została przywrócona.');
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
        
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <SafeIcon icon={RiHistoryLine} className="text-blue-600 text-xl" />
             </div>
             <div>
                <h2 className="text-xl font-bold text-gray-900">Historia rzutu</h2>
                <p className="text-xs text-gray-500 truncate max-w-[200px]">{projectName}</p>
             </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <SafeIcon icon={FiX} className="text-gray-400 text-lg" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {!projectId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6 text-center">
              <SafeIcon icon={RiHistoryLine} className="text-4xl mb-3 opacity-20" />
              <p className="text-sm">Zapisz projekt po raz pierwszy, aby rozpocząć tworzenie historii.</p>
            </div>
          ) : history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6 text-center">
              <SafeIcon icon={RiHistoryLine} className="text-4xl mb-3 opacity-20" />
              <p className="text-sm">Brak zapisanych wersji w historii dla tego projektu.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item, index) => (
                <div 
                  key={item.id || index}
                  className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-gray-100 rounded-md overflow-hidden flex-shrink-0 border border-gray-100">
                        {item.thumbnail ? (
                          <img src={item.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300">
                            <SafeIcon icon={RiTimeLine} />
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="font-medium text-gray-900 text-sm">
                          {format(item.timestamp, 'd MMMM yyyy', { locale: pl })}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <SafeIcon icon={RiTimeLine} className="text-[10px]" />
                          {format(item.timestamp, 'HH:mm:ss', { locale: pl })}
                        </div>
                        {index === 0 && (
                          <span className="inline-block mt-1 px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded font-medium">
                            Najnowsza
                          </span>
                        )}
                      </div>
                    </div>

                    <button 
                      onClick={() => handleRestore(item)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      title="Przywróć tę wersję"
                    >
                      <SafeIcon icon={FiRotateCcw} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPanel;